package com.shadowings.gero.data.lessons.chapter16

import com.shadowings.gero.data.lessons.adjectiveForms
import com.shadowings.gero.data.lessons.buildImperfectiveSentenceSync
import com.shadowings.gero.data.lessons.chapter11.buildPseudoverbalObjectSentence
import com.shadowings.gero.data.lessons.chapter11.buildWordBankFromWords
import com.shadowings.gero.data.lessons.distinctBaseWordCount
import com.shadowings.gero.data.lessons.possessiveForms
import com.shadowings.gero.data.repository.AdjectivePairs
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.PossessivePairs
import com.shadowings.gero.data.repository.SentenceOptions
import com.shadowings.gero.data.repository.SentenceResult
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.model.Verb
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_1
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_2
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_3
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_4
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_5
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_6
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_7
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_8
import gero.composeapp.generated.resources.exp_content_cambio_di_rotta_9
import gero.composeapp.generated.resources.exp_title_cambio_di_rotta
import kotlin.random.Random

// Masko's numbers (lines 4 and 5) come from the TSVs: CambioDiRottaCountTest recomputes them.
fun getCambioDiRottaContent(): List<IExplanation> {
    return listOf(
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_1,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_2,
            face = Faces.Idle,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_3,
            face = Faces.Happy,
            character = Characters.Masko,
        ),
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_4,
            face = Faces.Idle,
            character = Characters.Masko,
        ),
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_5,
            face = Faces.Happy,
            character = Characters.Masko,
        ),
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_6,
            face = Faces.Surprised,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_7,
            face = Faces.Doubt,
            character = Characters.Nilo,
        ),
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_8,
            face = Faces.Doubt,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_cambio_di_rotta,
            content = Res.string.exp_content_cambio_di_rotta_9,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
    )
}

// Whole sentences with a possessive and an adjective, never on the same noun: 5 verbal (sḏm.f
// with one object, wnm sꜣ.i t nfr "mio figlio mangia il pane buono") and 5 pseudoverbal (iw sš
// nfr ḥr iṯt ḥnqt.f "il buono scriba sta prendendo la sua birra"). The possessive goes on the
// subject, the object or the object's owner, and the builders put the adjective on a free noun.
fun getCambioDiRottaQuizContent(
    words: List<Word>,
    verbs: List<Verb>,
    languageCode: String,
    verbSentences: List<SentenceOptions> = emptyList(),
    random: Random = Random,
    possessivePairs: List<PossessivePairs> = DatabaseRepositoryImpl.possessivePairs,
    adjectivePairs: List<AdjectivePairs> = DatabaseRepositoryImpl.adjectivePairs
): List<IQuizContent> {
    val effectiveVerbSentences = verbSentences.ifEmpty { DatabaseRepositoryImpl.verbSentences }
    if (effectiveVerbSentences.none { distinctBaseWordCount(it.objects, words) >= 1 }) return emptyList()

    fun SentenceResult.translation(): String = when (languageCode) {
        "it" -> translationIt
        "de" -> translationDe
        "es" -> translationEs
        "fr" -> translationFr
        else -> translationEn
    }

    // A sentence counts only with its adjective: the builders leave it out when no free noun can
    // take one, so it's drawn again.
    val sentences = mutableListOf<SentenceResult>()
    fun addDistinct(count: Int, build: () -> SentenceResult) {
        repeat(count) {
            var candidate: SentenceResult? = null
            for (attempt in 0 until 20) {
                val built = build()
                if (!built.hasAdjective || sentences.any { it.translation() == built.translation() }) continue
                candidate = built
                break
            }
            candidate?.let { sentences.add(it) }
        }
    }

    // On the subject, on the object, or on the object's owner ("l'acqua di tuo padre").
    val possessiveCombos = listOf(Triple(true, false, false), Triple(false, true, false), Triple(false, false, true))
    addDistinct(5) {
        val (onSubject, onObject, onOwner) = possessiveCombos.random(random)
        buildImperfectiveSentenceSync(
            soggettiCount = 1,
            oggettiCount = 1,
            random = random,
            verbSentences = effectiveVerbSentences,
            words = words,
            verbs = verbs,
            subjectHavePossessive = onSubject,
            possessivePairs = possessivePairs,
            objectHavePossessive = onObject,
            objectPossessorHavePossessive = onOwner,
            haveAdjective = true,
            adjectivePairs = adjectivePairs
        )
    }
    addDistinct(5) {
        val (onSubject, onObject, onOwner) = possessiveCombos.random(random)
        // Nominal subject: a pronoun can take neither a possessive nor an adjective.
        val sentence = buildPseudoverbalObjectSentence(
            verbs = verbs,
            words = words,
            isPronominal = false,
            random = random,
            verbSentences = effectiveVerbSentences,
            subjectHavePossessive = onSubject,
            possessivePairs = possessivePairs,
            objectHavePossessive = onObject,
            objectPossessorHavePossessive = onOwner,
            haveAdjective = true,
            adjectivePairs = adjectivePairs
        )
        SentenceResult(
            hieroglyphs = sentence.hieroglyphs,
            transliteration = sentence.transliteration,
            translationIt = sentence.translationIt,
            translationEn = sentence.translationEn,
            translationDe = sentence.translationDe,
            translationEs = sentence.translationEs,
            translationFr = sentence.translationFr,
            alternativeTranslations = sentence.alternativeTranslations,
            hasAdjective = sentence.hasAdjective
        )
    }

    // Wrong tiles: a few seen words and verbs, plus two possessives and two adjective forms that
    // aren't in the sentence (another person, another gender or number).
    val possessives = possessiveForms(languageCode)
    val adjectives = adjectiveForms(words, adjectivePairs, languageCode)
    return sentences.shuffled(random).map { sentence ->
        val phrase = sentence.translation()
        val bank = buildWordBankFromWords(phrase, words, verbs, languageCode, random, distractorCount = 3)
        fun notInBank(forms: List<String>) = forms
            .filter { form -> bank.none { it.equals(form, ignoreCase = true) } }
            .shuffled(random)
            .take(2)
        val wrongPossessives = notInBank(possessives)
        val wrongAdjectives = notInBank(adjectives.filter { it !in wrongPossessives })
        WordSelectionContent(
            hieroglyphs = sentence.hieroglyphs,
            correctPhrase = phrase,
            alternativePhrases = sentence.alternativeTranslations[languageCode].orEmpty(),
            words = (bank + wrongPossessives + wrongAdjectives).shuffled(random),
            transliteration = sentence.transliteration,
            soggettiCount = 1
        )
    }
}
